import { OUT_BEAT, OUT_IDS } from '@/story/act1/out';
import { BeatSequencer } from '@/story/sequencer';
import { HedgerowOut } from '@/game/hedgerowOut';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/**
 * Beat 7, *Out* — the last beat of Act 1, as a part module.
 *
 * The field itself (dogs, boards, bloom, loops, placements) is `HedgerowOut`;
 * this is the seam that hands it to the host through the `BeatPartModule`
 * contract instead of nine inline call sites in `main.ts` (session 78).
 *
 * **Three orderings this module depends on, and each has a hook for it:**
 * the boards break in `beforeMove`, before the controller, because once Rapier
 * has met the intact collider she reports no speed; the placements go through
 * `moveSelf` inside `fixedStep`, so the geometric flags read where she was put;
 * and the cameras CUT (`framingCuts`), as they did when a legacy director held
 * them. What did move: the step now runs after `interaction.update` (the
 * contract's position for every part), so on the one tick she is placed, the
 * interaction system saw her where she stood before it. Her only interactable
 * is the bay, and the placements that matter are the ones that remove it.
 */
function createOutPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 7 / part 1: the host offered no world capability');
  const story = new BeatSequencer(OUT_BEAT);
  const field = new HedgerowOut(world);
  let disposed = false;
  return {
    story,
    interactables: field.interactables,
    get fade(): number { return field.director.fade; },
    get promptSuppressed(): boolean { return field.promptSuppressed; },
    framingCuts: true,
    framing: () => field.framing(),
    beforeMove(state, raiseFlag): void {
      field.tryBreakBoards(state, raiseFlag);
    },
    fixedStep(tick): void {
      const { moveSelf, nudged, rooted } = tick;
      if (moveSelf === undefined || nudged === undefined || rooted === undefined) {
        throw new Error('beat 7 / part 1: the tick must carry moveSelf, nudged and rooted');
      }
      field.step(tick.dt, tick.player, tick.raiseFlag);
      // `player` is the host's scratch copy from before this call, so after a
      // placement the flags must read the pose she was put at, not it.
      let at: { x: number; y: number; z: number } = tick.player;
      for (const pose of field.placements()) {
        moveSelf(pose);
        at = pose;
      }
      field.addStoryFlags({ add: tick.raiseFlag }, at);
      // Both verbs fail honestly (`HedgerowOut.answerVerb`).
      if (nudged(OUT_IDS.boards)) field.answerVerb('nudge', OUT_IDS.boards, tick.raiseFlag);
      if (rooted(OUT_IDS.boards)) field.answerVerb('root', OUT_IDS.boards, tick.raiseFlag);
    },
    handleCue(cue): void { field.handleCue(cue); },
    renderStep(frameDt): void {
      if (disposed) return;
      field.renderWorld(frameDt);
      field.renderAudio();
    },
    diagnostics: () => ({ kind: 'out', out: field.debug }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      field.dispose();
    },
  };
}

export const OUT_PART_MODULE: BeatPartModuleDefinition = {
  beat: 7,
  part: 1,
  room: 'hedgerow',
  create: createOutPartModule,
};
